/**
 * Turning `package_list` rows into `Package` (MYP-40).
 *
 * The same job `normaliseWarehouses` does in ship-to.ts, and written for the same reason: the
 * vendor's row is 86 fields of warehouse-operations bookkeeping — operator ids, split flags, bag
 * numbers, six pairs of date/operator stamps — and roughly fifteen of them are about the package
 * as a customer understands it. Nothing above this file sees the wire shape.
 *
 * Everything here is written against rows captured live from the endpoint, not from the API
 * document. The document shows `package_list` returning four fields; the live endpoint returns 86,
 * exactly as it under-reported `warehouses` as three fields against a real forty.
 */

import { packageDraftFrom } from './packages.js';
import type { Package, PackageDraft, PackageMessage, PackageStatus } from './packages.js';
import type { WarehouseService } from './warehouse-services.js';

/** A row as `package_list` sends it. Every value arrives as a string or null. */
type PackageRow = Readonly<Record<string, unknown>>;

/**
 * The vendor's twenty-two package statuses, mapped onto the six this app groups by. Taken from a
 * live `package_status_list` call — the API document names only two of them, and gets one wrong
 * (it glosses `NEW` as 新包裹 "new package"; the endpoint itself says 未抵达 "No Arrival", which is
 * a materially different thing to tell a customer).
 *
 * Unmapped codes fall to `on_the_way` and keep their own label, rather than being guessed into a
 * bucket — see `toPackageStatus`.
 */
const STATUS_GROUPS: Readonly<Record<string, PackageStatus>> = {
  // Not yet at the warehouse gate.
  UNCLAIM: 'on_the_way', // 未认领 Unclaimed
  NEW: 'on_the_way', // 未抵达 No Arrival
  DELAYIN: 'on_the_way', // 延迟抵达 Delayed

  ARRIVE: 'arrived', // 已抵达 Arrived
  IN: 'in_warehouse', // 进仓 Inbound

  // Committed to an order, not yet gone.
  ORDER: 'in_order', // 已下单 Ordered
  OUT: 'in_order', // 已捡单 Packed
  PACKED: 'in_order', // 完成打包称重 Weighed & Packed
  CONFIRMED: 'in_order', // 等待发货 Waiting Despatch

  // Gone — including every leg of the overseas-hub transfer chain, which is movement after
  // despatch rather than a different kind of state.
  DESPATCH: 'shipped', // 已发货 Despatched
  'DESPATCH OFFLINE': 'shipped', // 线下出货 Despatch Offline
  TRANSPORT: 'shipped', // 已转运 Transitted
  TRANSARRIVE: 'shipped', // 抵达海外仓 Arrived Hub
  TRANSFERPACKED: 'shipped', // 入仓中转 Inbound Transit
  TRANSFERWAITDESPATCH: 'shipped', // 中转出仓 Outbound Transit
  TRANSFERDISPATCH: 'shipped', // 发往海外仓 Transfer Hub
  TRANSFERPACKED2: 'shipped', // 海运待发 Waiting Despatch
  TRANSFERDISPATCH2: 'shipped', // 海运已发 Despatched

  // Stopped, not progressing. See `PackageStatus`'s doc comment.
  PACKAGELOSS: 'exception', // 货物遗失 Loss
  WAREHOUSEREJECTION: 'exception', // 仓库拒收 Rejected
  DESTROY: 'exception', // 销毁 Destroyed
  RETURN: 'exception', // 已退货 Returned
};

/**
 * A status code's group. An unrecognised code lands in `on_the_way` — the only bucket whose
 * meaning ("somewhere before the warehouse") is not a claim the app can be wrong about in a way
 * that hides a problem, unlike `shipped` (implies it left) or `in_warehouse` (implies it arrived).
 * Its own label still renders verbatim, so the row tells the truth even when the grouping cannot.
 */
export function toPackageStatus(code: string): PackageStatus {
  return STATUS_GROUPS[code.trim().toUpperCase()] ?? 'on_the_way';
}

/** Every status code this app knows how to group, for tests and for spotting vendor additions. */
export const KNOWN_PACKAGE_STATUS_CODES: readonly string[] = Object.keys(STATUS_GROUPS);

/**
 * The vendor codes behind one or more of this app's groups — the reverse of `toPackageStatus`,
 * for asking the server for a tab's packages (MYP-126). In the order of the table above.
 */
export function packageStatusCodesFor(groups: readonly PackageStatus[]): readonly string[] {
  return KNOWN_PACKAGE_STATUS_CODES.filter((code) => {
    const group = STATUS_GROUPS[code];
    return group !== undefined && groups.includes(group);
  });
}

/** Which language a status label resolves to. The vendor stores only Chinese and English. */
export type PackageStatusLanguage = 'zh' | 'en';

/** `status_en`/`status_cn` per code, as `package_status_list` returns them. */
export interface PackageStatusEntry {
  readonly code: string;
  readonly labelEn: string;
  readonly labelCn: string;
}

function text(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function digits(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * `"2020-12-09 10:10:16"` → ISO 8601.
 *
 * The vendor sends MySQL datetimes with no zone. They are written in its own +08:00 (the
 * warehouses are in China, the customers in Malaysia — the same offset either way), so the offset
 * is pinned here rather than left to `new Date()` to infer from whatever machine is rendering.
 * Without that, the same intake date reads as a different day for a customer travelling, and the
 * days-in-warehouse count moves with them.
 */
export function toIsoTimestamp(value: unknown): string | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;

  // The vendor writes a zeroed stamp for "never happened" on some columns.
  if (raw.startsWith('0000-00-00')) return undefined;

  const parsed = new Date(`${raw.replace(' ', 'T')}+08:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/** `package_status_list` rows → the dictionary `normalisePackages` resolves labels against. */
export function normalisePackageStatuses(rows: readonly unknown[]): readonly PackageStatusEntry[] {
  const entries: PackageStatusEntry[] = [];

  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const record = row as PackageRow;
    const code = text(record['status']);
    if (code === undefined) continue;

    entries.push({
      code,
      labelEn: text(record['status_en']) ?? code,
      labelCn: text(record['status_cn']) ?? text(record['status_zh']) ?? code,
    });
  }

  return entries;
}

/** A goods-type code with both of the vendor's labels, as `goods_type` publishes them. */
export interface GoodsTypeEntry {
  readonly code: string;
  readonly labelEn: string;
  readonly labelCn: string;
}

/**
 * `goods_type` → the goods-type dictionary (MYP-102).
 *
 * Takes the endpoint's *keyed* map, not a row list. The document shows `{id, name}` rows; the live
 * endpoint sends none — it keys each entry by its code and carries only the labels in the value:
 *
 * ```
 * {"GENERAL": {"en": "General", "zh": "普货", "cn": "普货"}, "SENSITIVE": {...}}
 * ```
 *
 * So the code exists only as the key. `normaliseEnvelope` hands a keyed map back as `list` (the
 * values) plus `keyed` (the original); reading `list` here would keep the labels and silently lose
 * every code. The caller passes `keyed`.
 *
 * A missing label falls back to the code rather than to an invented word, and an entry that is not
 * an object is skipped. Like the status list, there is no Malay — callers read the English side.
 */
export function normaliseGoodsTypes(
  keyed: Readonly<Record<string, unknown>>,
): readonly GoodsTypeEntry[] {
  const entries: GoodsTypeEntry[] = [];

  for (const [key, value] of Object.entries(keyed)) {
    const code = text(key);
    if (code === undefined || typeof value !== 'object' || value === null) continue;
    const record = value as PackageRow;

    entries.push({
      code,
      labelEn: text(record['en']) ?? code,
      labelCn: text(record['zh']) ?? text(record['cn']) ?? code,
    });
  }

  return entries;
}

/**
 * The label for one goods-type code, in the reader's language. A code the dictionary does not
 * carry comes back as itself — shown raw, never guessed and never dropped.
 */
export function goodsTypeLabel(
  entries: readonly GoodsTypeEntry[],
  code: string,
  language: PackageStatusLanguage,
): string {
  const entry = entries.find((candidate) => candidate.code === code);
  if (!entry) return code;
  return language === 'zh' ? entry.labelCn : entry.labelEn;
}

/**
 * The goods-type codes the package filter offers: the vendor's dictionary in its own order, then any
 * code a package carries that the dictionary does not — appended raw, in the order first seen.
 *
 * The second half is the unknown-code rule applied to a filter: a package filed under a code the
 * vendor has not published (yet) stays findable by it, rather than silently vanishing from every
 * option but "All". A package with no goods type at all adds nothing — there is no code to offer.
 */
export function goodsTypeFilterCodes(
  entries: readonly { readonly code: string }[],
  packages: readonly Package[],
): readonly string[] {
  const codes = entries.map((entry) => entry.code);
  const seen = new Set(codes);

  for (const pkg of packages) {
    if (pkg.goodsType === undefined || seen.has(pkg.goodsType)) continue;
    seen.add(pkg.goodsType);
    codes.push(pkg.goodsType);
  }

  return codes;
}

/**
 * `packages_services` → each warehouse's own service list (MYP-100), keyed by warehouse id.
 *
 * Takes the endpoint's *keyed* map. Live, `data.list` is `{"1": [...11 rows], "3": [...], ...}` —
 * one array of service rows per warehouse id, not the one-record-per-key the document sketches.
 * The grouping is the point: it is what says which warehouse offers what, so this reads `keyed`
 * rather than the envelope's flattened `list` — as `normaliseOrderServices` does since MYP-119.
 *
 * Per row: the name in the reader's language with the other language as `nameNative`, the notice
 * in the reader's language, `confirm: "Y"` as `requiresConfirmation`, the price from `fee` (never
 * `defaultfee`, MYP-80) and the picture from `img` (MYP-116). A row is keyed by its own
 * `warehouseid` when it carries one, falling back to the group key it arrived under; the two agree
 * on every live row.
 *
 * Every warehouse id the vendor sends is kept, including ones the `warehouses` endpoint does not
 * list (live: 11, 12, 13). They are harmless — nothing ever asks for them — and dropping them
 * here would mean this adapter knowing about a different endpoint.
 */
export function normalisePackageServices(
  keyed: Readonly<Record<string, unknown>>,
  language: PackageStatusLanguage,
): Readonly<Record<string, readonly WarehouseService[]>> {
  const byWarehouse: Record<string, WarehouseService[]> = {};

  for (const [key, group] of Object.entries(keyed)) {
    // Live groups are arrays; a single record under a key is read as a one-row group.
    const rows = Array.isArray(group) ? group : [group];

    for (const row of rows) {
      if (typeof row !== 'object' || row === null) continue;
      const record = row as PackageRow;

      const id = text(record['id']);
      const nameCn = text(record['name_cn']) ?? text(record['name']);
      const nameEn = text(record['name_en']) ?? text(record['englishname']);
      const name = language === 'zh' ? (nameCn ?? nameEn) : (nameEn ?? nameCn);
      const warehouseId = text(record['warehouseid']) ?? text(key);
      if (id === undefined || name === undefined || warehouseId === undefined) continue;

      const other = language === 'zh' ? nameEn : nameCn;
      const description =
        language === 'zh'
          ? (text(record['remark_cn']) ?? text(record['remark']) ?? text(record['remark_en']))
          : (text(record['remark_en']) ?? text(record['remark_cn']) ?? text(record['remark']));

      const list = (byWarehouse[warehouseId] ??= []);
      if (list.some((service) => service.id === id)) continue;

      const fee = digits(record['fee']);
      list.push({
        id,
        name,
        nameNative: other !== undefined && other !== name ? other : undefined,
        description: description ?? '',
        requiresConfirmation: text(record['confirm'])?.toUpperCase() === 'Y',
        ...(fee !== undefined
          ? { feeCents: Math.round(fee * 100), currency: text(record['currency']) }
          : {}),
        ...(text(record['img']) !== undefined ? { imageUrl: text(record['img']) } : {}),
      });
    }
  }

  return byWarehouse;
}

/**
 * One row → one `Package`, or `undefined` for a row with no usable identity.
 *
 * Two fields the list screen renders have no source on this endpoint and are always left unset:
 *
 * - `dimensionsCm` — the row carries `vol` (a single volume figure) and no length/width/height, so
 *   MYP-37's dimensions line has nothing to show. It already renders conditionally.
 * - `serviceCodes` — `pkgservices` is an input to `add_packages` and does not come back on the
 *   row, so a package's applied services cannot be read back. The add/edit sheet still offers the
 *   list from `packages_services`; it just cannot show what was previously chosen.
 *
 * Both are gaps in the endpoint, not omissions here, and both want raising with the vendor.
 */
export function normalisePackage(
  row: unknown,
  statuses: readonly PackageStatusEntry[],
  language: PackageStatusLanguage,
): Package | undefined {
  if (typeof row !== 'object' || row === null) return undefined;
  const record = row as PackageRow;

  const id = text(record['id']) ?? text(record['pkg_id']);
  const trackingNumber = text(record['inorderno']);
  if (id === undefined || trackingNumber === undefined) return undefined;

  const statusCode = text(record['status']) ?? 'NEW';
  const entry = statuses.find((candidate) => candidate.code === statusCode);
  const statusLabel =
    (language === 'zh' ? entry?.labelCn : entry?.labelEn) ??
    // The row's own `status_cn` is the only label it carries; better than the bare code when the
    // dictionary fetch failed, and still correct for a Chinese reader.
    text(record['status_cn']) ??
    statusCode;

  // Weight arrives in kilograms as a decimal string ("0.5"); the app holds integer grams.
  const weightKg = digits(record['weight']) ?? digits(record['uploadweight']);
  // A declared price is a decimal amount in the row's own currency; the app holds integer cents.
  const declaredValue = digits(record['declareprice']);
  // Not shown — carried so an edit can send them back unchanged (MYP-123, `toUpdatePackageParams`).
  const parcelCount = parcelCountOf(record['pkgnum']);
  const transportCode = text(record['transtype']);

  return {
    id,
    trackingNumber,
    itemNameEn: text(record['declarename']) ?? trackingNumber,
    itemNameCn: text(record['declarecnname']),
    quantity: digits(record['declarenum']) ?? 1,
    warehouseId: text(record['warehouseid']) ?? '',
    status: toPackageStatus(statusCode),
    statusCode,
    statusLabel,
    // `indate` is the intake stamp proper; `arrivedtime` is the gate scan that precedes it. Intake
    // is what "days in warehouse" and the storage clock are counted from, so it wins when both are
    // present — see `Package.arrivedAt`.
    arrivedAt: toIsoTimestamp(record['indate']) ?? toIsoTimestamp(record['arrivedtime']),
    weightGrams: weightKg === undefined ? undefined : Math.round(weightKg * 1000),
    dimensionsCm: undefined,
    shelfLocation: text(record['whno']),
    courierName: text(record['expresscompany']),
    declaredValueCents: declaredValue === undefined ? undefined : Math.round(declaredValue * 100),
    remark: text(record['remarks']),
    serviceCodes: [],
    orderNumber: text(record['outorderno']),
    // Left unset rather than defaulted to GENERAL: live rows do arrive with a null `goodstype`
    // (4 of 309 on the test account, all old), and filing those under "General" would count them
    // in a filter they were never declared for.
    goodsType: text(record['goodstype']),
    ...(parcelCount !== undefined ? { parcelCount } : {}),
    ...(transportCode !== undefined ? { transportCode } : {}),
  };
}

/** `pkgnum` as a whole number of parcels, or undefined when missing, zero or not a number. */
function parcelCountOf(value: unknown): number | undefined {
  const count = digits(value);
  return count !== undefined && Number.isInteger(count) && count > 0 ? count : undefined;
}

/**
 * `get_packages_msg` rows → `PackageMessage`.
 *
 * The document lists four fields here (`id`, `tid`, `type`, `content`) and omits the two the
 * thread cannot be drawn without: `direct`, which decides the side a bubble sits on, and
 * `msgdate`, its timestamp. Both are on the live row, along with a `title`, a `username` for the
 * warehouse staffer who wrote it, and an unused `readflag`.
 *
 * `content` arrives as HTML — entities (`&sup2;`, `&nbsp;`) and Word's own export markup
 * (`<o:p>`, `mso-*` styles, occasional Wingdings glyphs). It is passed through verbatim here on
 * purpose: sanitising is a security boundary and belongs in one reviewed place, not improvised
 * inside a data adapter. `sanitiseMessageContent` (`message-sanitize.ts`, MYP-39) is that place —
 * called once, at render, in `PackageMessagesSheet`'s `MessageBubble` — and reduces it to plain
 * text rather than rendering it as HTML, which is what would actually open the XSS risk this
 * staff-authored field carries.
 */
export function normalisePackageMessages(rows: readonly unknown[]): readonly PackageMessage[] {
  const messages: PackageMessage[] = [];

  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue;
    const record = row as PackageRow;

    const id = text(record['id']);
    const packageId = text(record['tid']);
    const content = text(record['content']);
    if (id === undefined || packageId === undefined || content === undefined) continue;

    messages.push({
      id,
      packageId,
      // U2W is the safer default: attributing an unlabelled message to the warehouse would put
      // words in its mouth, while the customer's own side carries no sender name to be wrong about.
      direction: text(record['direct'])?.toUpperCase() === 'W2U' ? 'W2U' : 'U2W',
      content,
      createdAt: toIsoTimestamp(record['msgdate']) ?? new Date(0).toISOString(),
    });
  }

  return messages;
}

/** Every usable row, in the order the vendor sent them. Unusable rows are dropped, not faked. */
export function normalisePackages(
  rows: readonly unknown[],
  statuses: readonly PackageStatusEntry[],
  language: PackageStatusLanguage,
): readonly Package[] {
  const packages: Package[] = [];

  for (const row of rows) {
    const parsed = normalisePackage(row, statuses, language);
    if (parsed) packages.push(parsed);
  }

  return packages;
}

/* -------------------------------------------------------------------------------------------- *
 * Writes (MYP-98)
 * -------------------------------------------------------------------------------------------- */

/** Integer cents → the vendor's decimal amount (`1999` → `"19.99"`), with no float arithmetic. */
function centsToDecimal(cents: number): string {
  const whole = Math.trunc(cents / 100);
  const fraction = cents % 100;
  return `${whole}.${String(fraction).padStart(2, '0')}`;
}

/**
 * A package draft → the `add_packages` form fields (MYP-98).
 *
 * Only what the customer actually gave is sent. An empty optional field is left out rather than
 * posted as `""`, so the vendor applies its own default instead of storing a blank — and three
 * fields the form does not collect are left out entirely, on purpose:
 *
 * - `transtype` — the document says the vendor writes UNC when it is empty. Leaving it out takes
 *   that documented path rather than claiming AIR or SEA on the customer's behalf; the warehouse
 *   decides transport when the package joins an order.
 * - `revname` (claimant name) — optional, and nothing here knows who the customer means.
 * - `pkgnum` (parcel count) — documented as defaulting to 1, which is what one tracking number is.
 *
 * `declareprice` is a decimal amount, not cents; a negative or non-integer cents value (which the
 * form cannot produce, but a caller could) is left out rather than sent. `pkgservices` is the
 * chosen service ids comma-joined, the format the document's own example shows (`"5,6"`).
 */
export function toAddPackageParams(draft: PackageDraft): Readonly<Record<string, string>> {
  const params: Record<string, string> = {
    inorderno: draft.trackingNumber.trim(),
    declarename: draft.itemNameEn.trim(),
    declarenum: String(draft.quantity),
    warehouseid: draft.warehouseId,
  };

  const optional: Record<string, string | undefined> = {
    expresscompany: draft.courierName.trim() || undefined,
    declarecnname: draft.itemNameCn.trim() || undefined,
    goodstype: draft.goodsType || undefined,
    remarks: draft.remark.trim() || undefined,
    pkgservices: draft.serviceIds.length > 0 ? draft.serviceIds.join(',') : undefined,
    declareprice:
      draft.declaredValueCents !== undefined &&
      Number.isInteger(draft.declaredValueCents) &&
      draft.declaredValueCents >= 0
        ? centsToDecimal(draft.declaredValueCents)
        : undefined,
  };

  for (const [key, value] of Object.entries(optional)) {
    if (value !== undefined) params[key] = value;
  }

  return params;
}

/**
 * An edited package → the `add_packages` form fields that update it (MYP-123).
 *
 * The vendor's answer to question 7: there is no update endpoint; resubmit the package through
 * `add_packages` with the same data, and because the tracking number already exists the server
 * updates that package instead of adding one. So this is `toAddPackageParams` with the rules an
 * update needs on top:
 *
 * - **Identity comes from the package, never the form.** `inorderno` is what the server matches
 *   on and `warehouseid` cannot move, so both are the original's — a stray edit to either in the
 *   draft cannot turn an update into a second package.
 * - **A field can be changed, not emptied.** Live (6 Oct 2026, package 8115756522573): sending
 *   `remarks=""` answered "success" and left the old remark in place — the vendor ignores an empty
 *   value on update. So an emptied courier, Chinese name, remark or declared value sends the
 *   stored value back instead, and `packageDraftChanged` treats it as no change, rather than
 *   telling the customer "saved" for something that did not happen.
 * - **Hidden fields go back unchanged.** `pkgnum` and `transtype` are not on the form, and left out
 *   the vendor would reset them to 1 and UNC.
 * - **No `pkgservices`, ever.** `package_list` never says which services a package has, so there is
 *   nothing true to send; the form hides services in Edit.
 */
export function toUpdatePackageParams(
  draft: PackageDraft,
  original: Package,
): Readonly<Record<string, string>> {
  const params: Record<string, string> = {
    ...toAddPackageParams({ ...draft, serviceIds: [] }),
    inorderno: original.trackingNumber,
    warehouseid: original.warehouseId,
  };

  const keepOrChange = (key: string, edited: string, stored: string | undefined) => {
    const value = edited.trim() || stored?.trim() || '';
    if (value) params[key] = value;
    else delete params[key];
  };
  keepOrChange('expresscompany', draft.courierName, original.courierName);
  keepOrChange('declarecnname', draft.itemNameCn, original.itemNameCn);
  keepOrChange('remarks', draft.remark, original.remark);

  // An emptied declared value would be ignored too (see above): send the stored one back.
  if (params['declareprice'] === undefined && original.declaredValueCents !== undefined) {
    params['declareprice'] = centsToDecimal(original.declaredValueCents);
  }
  const goodsType = draft.goodsType || original.goodsType;
  if (goodsType) params['goodstype'] = goodsType;
  if (original.parcelCount !== undefined) params['pkgnum'] = String(original.parcelCount);
  if (original.transportCode !== undefined) params['transtype'] = original.transportCode;
  delete params['pkgservices'];

  return params;
}

/**
 * Whether an edit changed anything the update would send (MYP-123) — so Save stays off for an
 * untouched form rather than posting the same package back. Compared on the trimmed values the
 * request carries, so a trailing space alone is not a change.
 */
export function packageDraftChanged(draft: PackageDraft, original: Package): boolean {
  const before = toUpdatePackageParams(packageDraftFrom(original), original);
  const after = toUpdatePackageParams(draft, original);
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const key of keys) if (before[key] !== after[key]) return true;
  return false;
}

/**
 * Whether a tracking number is already on the account — checked before `add_packages` is called,
 * so an obvious duplicate is caught without a round trip to the vendor at all. Trimmed and
 * case-insensitive, since couriers print these in either case and customers paste stray spaces.
 * The vendor still has the final say (it rejects duplicates itself); this only spares the request.
 */
export function isTrackingNumberOnAccount(
  packages: readonly Package[],
  trackingNumber: string,
): boolean {
  const wanted = trackingNumber.trim().toUpperCase();
  if (wanted === '') return false;
  return packages.some((pkg) => pkg.trackingNumber.trim().toUpperCase() === wanted);
}
